
import { useState, useEffect } from 'react';

export default function Contact() {
  // Add Schema.org JSON-LD for Contact page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact - StarSales B.V.",
      "description": "Neem contact op met StarSales B.V. voor websites, Google Ads en online marketing. Vraag een gratis offerte aan.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "StarSales B.V.",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+31-970-102-241-37",
          "contactType": "customer service",
          "email": "info@starsales.nl",
          "availableLanguage": "Dutch",
          "hoursAvailable": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday", 
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "17:00"
          }
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Mgr. Cornelis Veermanlaan 63",
          "addressLocality": "Volendam",
          "postalCode": "1131 KE",
          "addressCountry": "NL"
        }
      }
    });
    document.head.appendChild(script);

    // Add page-specific meta tags
    document.title = "Contact - StarSales B.V. | Vraag een Gratis Offerte Aan";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Neem contact op met StarSales B.V. voor websites, Google Ads en online marketing. ✓ Gratis offerte ✓ Binnen 24 uur reactie ✓ Tel: 0970 102 241 37');
    }

    return () => {
      document.head.removeChild(script);
      document.title = "StarSales B.V. – Websites & Online Marketing voor ZZP en MKB | Volendam";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Websites, Google Ads en social media advertenties voor ZZP en kleine bedrijven. Vraag een gratis marketingcheck aan. ✓ Snel live ✓ Meetbare resultaten');
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    naam: '',
    bedrijf: '',
    email: '',
    telefoon: '',
    hulpvraag: '',
    budget: '',
    bericht: '',
    avgCheck: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.avgCheck) {
      alert('Je moet akkoord gaan met de privacyverklaring om het formulier te versturen.');
      return;
    }

    if (formData.bericht.length > 500) {
      alert('Je bericht mag maximaal 500 karakters bevatten.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3j3mc5soafcrsrcq980', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          naam: '',
          bedrijf: '',
          email: '',
          telefoon: '',
          hulpvraag: '',
          budget: '',
          bericht: '',
          avgCheck: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold text-[#0A66C2]" style={{ fontFamily: '"Pacifico", serif' }}>
                StarSales
              </a>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#0A66C2] font-medium transition-colors">Home</a>
              <a href="/over-ons" className="text-gray-700 hover:text-[#0A66C2] font-medium transition-colors">Over ons</a>
              <a href="/contact" className="text-gray-900 hover:text-[#0A66C2] font-medium transition-colors">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <a href="tel:097010224137" className="text-[#0A66C2] hover:text-[#F57C00] font-medium transition-colors">
                0970 102 241 37
              </a>
              <button className="bg-[#F57C00] text-white px-4 py-2 rounded-full font-medium hover:bg-[#E65100] transition-colors whitespace-nowrap cursor-pointer">
                Gratis check
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A66C2] to-[#1976D2] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Klaar om te <span className="text-[#F57C00]">groeien?</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto" style={{ fontFamily: '"Inter", sans-serif' }}>
              Vraag een gratis marketingcheck of offerte aan en ontdek hoe wij jouw bedrijf online kunnen laten groeien.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Vraag een offerte aan
              </h2>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                    <p className="text-green-800 font-medium">Bedankt! We nemen binnen 24 uur contact met je op.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                    <p className="text-red-800 font-medium">Er ging iets mis. Probeer het opnieuw of bel ons direct.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="naam" className="block text-sm font-semibold text-gray-700 mb-2">
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="naam"
                      name="naam"
                      value={formData.naam}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm"
                      placeholder="Je volledige naam"
                    />
                  </div>
                  <div>
                    <label htmlFor="bedrijf" className="block text-sm font-semibold text-gray-700 mb-2">
                      Bedrijf
                    </label>
                    <input
                      type="text"
                      id="bedrijf"
                      name="bedrijf"
                      value={formData.bedrijf}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm"
                      placeholder="Je bedrijfsnaam"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm"
                      placeholder="je@email.nl"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefoon" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefoon
                    </label>
                    <input
                      type="tel"
                      id="telefoon"
                      name="telefoon"
                      value={formData.telefoon}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm"
                      placeholder="06 12345678"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="hulpvraag" className="block text-sm font-semibold text-gray-700 mb-2">
                    Waar kunnen we je mee helpen? *
                  </label>
                  <div className="relative">
                    <select
                      id="hulpvraag"
                      name="hulpvraag"
                      value={formData.hulpvraag}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm pr-8 appearance-none cursor-pointer"
                    >
                      <option value="">Selecteer een optie</option>
                      <option value="website">Nieuwe website</option>
                      <option value="google-ads">Google Ads</option>
                      <option value="social-ads">Social Media Advertenties</option>
                      <option value="seo">SEO & Content</option>
                      <option value="hosting">Hosting & Onderhoud</option>
                      <option value="alles">Complete online strategie</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                    Indicatief budget
                  </label>
                  <div className="relative">
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm pr-8 appearance-none cursor-pointer"
                    >
                      <option value="">Selecteer budget</option>
                      <option value="<500">&lt; €500</option>
                      <option value="500-1500">€500 - €1.500</option>
                      <option value="1500-3000">€1.500 - €3.000</option>
                      <option value=">3000">&gt; €3.000</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="bericht" className="block text-sm font-semibold text-gray-700 mb-2">
                    Bericht
                  </label>
                  <textarea
                    id="bericht"
                    name="bericht"
                    value={formData.bericht}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent text-sm resize-none"
                    placeholder="Vertel ons meer over je project of stel je vraag..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.bericht.length}/500 karakters
                  </p>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="avgCheck"
                    name="avgCheck"
                    checked={formData.avgCheck}
                    onChange={handleInputChange}
                    required
                    className="mt-1 mr-3 cursor-pointer"
                  />
                  <label htmlFor="avgCheck" className="text-sm text-gray-700 cursor-pointer">
                    Ik ga akkoord met de <a href="/privacy" className="text-[#0A66C2] hover:text-[#F57C00] underline">privacyverklaring</a> en geef toestemming voor het verwerken van mijn gegevens. *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F57C00] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#E65100] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Versturen...' : 'Offerte aanvragen'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  Contactgegevens
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0A66C2] text-white rounded-lg flex items-center justify-center mr-4 mt-1">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Adres</h4>
                      <p className="text-gray-600">
                        Mgr. Cornelis Veermanlaan 63<br />
                        1131 KE Volendam
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0A66C2] text-white rounded-lg flex items-center justify-center mr-4 mt-1">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Telefoon</h4>
                      <a href="tel:097010224137" className="text-[#0A66C2] hover:text-[#F57C00] transition-colors">
                        0970 102 241 37
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#0A66C2] text-white rounded-lg flex items-center justify-center mr-4 mt-1">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">E-mail</h4>
                      <a href="mailto:info@starsales.nl" className="text-[#0A66C2] hover:text-[#F57C00] transition-colors">
                        info@starsales.nl
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-[#F57C00] text-white rounded-lg flex items-center justify-center mr-4 mt-1">
                      <i className="ri-whatsapp-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                      <a href="https://wa.me/31852502651" className="text-[#0A66C2] hover:text-[#F57C00] transition-colors">
                        Chat via WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  Openingstijden
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Maandag - Vrijdag</span>
                    <span className="font-semibold text-gray-900">09:00 - 17:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Weekend</span>
                    <span className="font-semibold text-gray-900">Gesloten</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <i className="ri-information-line mr-2"></i>
                    Buiten kantooruren? Laat een bericht achter en we nemen de volgende werkdag contact op.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Bezoek ons kantoor
            </h2>
            <p className="text-xl text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
              Kom langs voor een persoonlijk gesprek in het hart van Volendam
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2426.123456789!2d5.0123456!3d52.4987654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMgr.%20Cornelis%20Veermanlaan%2063%2C%201131%20KE%20Volendam!5e0!3m2!1snl!2snl!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <span className="text-3xl font-bold text-[#0A66C2]" style={{ fontFamily: '"Pacifico", serif' }}>
                  StarSales
                </span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md" style={{ fontFamily: '"Inter", sans-serif' }}>
                StarSales B.V. helpt ZZP en kleine bedrijven groeien met websites, Google Ads, social media advertenties en meer.
              </p>
              <div className="space-y-2 text-gray-300">
                <p>Mgr. Cornelis Veermanlaan 63</p>
                <p>1131 KE Volendam</p>
                <p>KvK: 97377562</p>
                <p>Tel: 0970 102 241 37</p>
                <p>E-mail: info@starsales.nl</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Diensten
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Websites</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Google Ads</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Social Media Ads</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">SEO</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Hosting</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Links
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/privacy" className="hover:text-[#F57C00] transition-colors">Privacy</a></li>
                <li><a href="/cookies" className="hover:text-[#F57C00] transition-colors">Cookies</a></li>
                <li><a href="/voorwaarden" className="hover:text-[#F57C00] transition-colors">Voorwaarden</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 StarSales B.V. Alle rechten voorbehouden.
            </p>
            <div className="flex space-x-4 mt-5 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
